import React from 'react';
import { Link } from 'react-router-dom';
import { Save, ArrowRight, History, Trash2, ExternalLink } from 'lucide-react';
import PropTypes from 'prop-types';
import { getTextBoxColor } from '../features/edit-page/model/textBoxColors';

function EditableTextInputs({
  pageLanguage,
  textByLang,
  setTextByLang,
  savingFields,
  saveTextChanges,
  translateText,
  pageId,
  bookId,
  additionalEntries = [],
  layoutItems = [],
  onAddToLayout,
  onToggleLayoutBox,
  layoutBusy = false,
  onUpdateAdditionalEntryText,
  onSaveAdditionalEntries,
  onRemoveAdditionalEntry,
  onRenameAdditionalEntry,
  renamingText = false,
}) {
  const languages = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Spanish' },
    { code: 'de', label: 'German' },
    { code: 'nl', label: 'Dutch' },
    { code: 'ru', label: 'Russian' },
    { code: 'it', label: 'Italian' },
    { code: 'fr', label: 'French' },
    { code: 'tr', label: 'Turkish' },
    { code: 'pl', label: 'Polish' },
  ];

  const renderLayoutAction = (entryId) => {
    if (!onAddToLayout) return null;
    const boxes = layoutItems.filter((item) => entryId
      ? item.sourceTextEntryId === entryId
      : item.role === 'main' || item.sourceType === 'page_main');
    const hidden = boxes.length > 0 && boxes.every((item) => item.visibleOnCanvas === false);
    if (boxes.length && !hidden) return null;
    return <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 p-2">
      <span className="text-xs font-semibold text-slate-600">{hidden ? 'Hidden from canvas' : 'Unused text'}</span>
      <button type="button" disabled={layoutBusy} onClick={() => hidden ? boxes.forEach((item) => onToggleLayoutBox?.(item.id)) : onAddToLayout(entryId)}
        className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-800 disabled:opacity-40">{hidden ? 'Show on canvas' : 'Add to layout'}</button>
    </div>;
  };

  return (
    <section className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-950">Text and translations</h2>
          <p className="text-xs text-slate-500">Edit and save wording here. Select a box above to change its style and placement.</p>
        </div>
        {bookId && (
          <Link
            to={`/book-translations/${bookId}?plang=${pageLanguage}`}
            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 shadow-sm hover:bg-emerald-50"
          >
            All translations
            <ExternalLink size={13} />
          </Link>
        )}
      </div>
      <div className="space-y-4">
        <section className="rounded-xl border border-emerald-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-slate-950">Main text</h3>
            <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold uppercase text-emerald-800">Main</span>
          </div>
          {renderLayoutAction(null)}
          <div className="space-y-4">
            {languages.map(({ code, label }) => {
              const shouldShow = pageLanguage ? pageLanguage.includes(code) || code === 'en' : code === 'en';
              if (!shouldShow) return null;
              const field = `text_${code}`;
              const text = textByLang[code] || '';

              return (
                <div key={field} className="border-t border-emerald-100 pt-3 first:border-0 first:pt-0">
                  <label className="mb-2 block text-sm font-semibold text-slate-800">{label}</label>
                  <textarea
                    value={text}
                    onChange={(e) => setTextByLang((prev) => ({ ...prev, [code]: e.target.value }))}
                    rows="5"
                    className="w-full rounded-lg border border-emerald-100 bg-emerald-50/30 p-3 text-sm outline-none focus:border-emerald-500 focus:bg-white"
                    data-testid={`language-text-${code}`}
                  />
                  <div className="mt-2 flex flex-wrap justify-start gap-2">
                    <button type="button" onClick={() => saveTextChanges(field, text)} className="flex items-center justify-center rounded bg-white p-2 shadow hover:bg-emerald-50" aria-label={`Save ${label} text`} title={`Save ${label} text`}>
                      {savingFields[field] ? 'Saving...' : ''}<Save size={20} />
                    </button>
                    <Link to={`/history/${pageId}`} className="flex items-center justify-center rounded bg-white p-2 shadow hover:bg-emerald-50" aria-label={`View ${label} text history`} title={`View ${label} text history`}><History size={20} /></Link>
                    {code !== 'en' && <button type="button" onClick={() => translateText(code)} className="flex items-center gap-2 rounded bg-emerald-700 px-4 py-2 text-white hover:bg-emerald-800">Translate English <ArrowRight size={20} /> {label}</button>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {additionalEntries.map((entry, index) => (
          <section key={entry.id} className={`rounded-xl border p-4 ${getTextBoxColor(index).card}`}>
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-bold text-slate-950">{entry.label || `Text box ${index + 1}`}</h3>
                {onRenameAdditionalEntry && <button type="button" disabled={renamingText} aria-label={`Rename ${entry.label || `Text box ${index + 1}`}`} onClick={() => onRenameAdditionalEntry(entry.id)} className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 disabled:opacity-40">Rename</button>}
              </div>
              <span className={`rounded-full px-2 py-1 text-[11px] font-semibold uppercase ${getTextBoxColor(index).badge}`}>Text box {index + 1}</span>
            </div>
            {renderLayoutAction(entry.id)}
            <div className="space-y-4">
              {languages.map(({ code, label }) => {
                const shouldShow = pageLanguage ? pageLanguage.includes(code) || code === 'en' : code === 'en';
                if (!shouldShow) return null;
                return (
                  <div key={`${entry.id}-${code}`} className="border-t border-emerald-100 pt-3 first:border-0 first:pt-0">
                    <label className="mb-2 block text-sm font-semibold text-slate-800">{label}</label>
                    <textarea
                      value={entry.texts?.[code] || ''}
                      onChange={(event) => onUpdateAdditionalEntryText?.(entry.id, code, event.target.value)}
                      rows="5"
                    className={`w-full rounded-lg border p-3 text-sm outline-none focus:bg-white ${getTextBoxColor(index).field}`}
                      data-testid={`additional-language-text-${entry.id}-${code}`}
                    />
                    <div className="mt-2 flex flex-wrap justify-start gap-2">
                      <button type="button" onClick={() => onSaveAdditionalEntries?.()} className="flex items-center justify-center rounded bg-white p-2 shadow hover:bg-emerald-50" title="Save additional text"><Save size={20} /></button>
                      <Link to={`/history/${pageId}`} className="flex items-center justify-center rounded bg-white p-2 shadow hover:bg-emerald-50" aria-label={`View ${label} history for ${entry.label || 'extra text'}`} title={`View ${label} history` }><History size={20} /></Link>
                      {onRemoveAdditionalEntry && <button type="button" onClick={() => { if (window.confirm('Permanently delete this text and all its translations? Its layout boxes will also be deleted. This cannot be undone.')) onRemoveAdditionalEntry(entry.id); }} className="inline-flex rounded border border-red-200 bg-white p-2 text-red-700 shadow hover:bg-red-50" title="Delete text entry and translations" aria-label={`Delete ${entry.label || 'text entry'} and its translations`}><Trash2 size={14} /></button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}

EditableTextInputs.propTypes = {
  pageLanguage: PropTypes.string.isRequired,
  textByLang: PropTypes.shape({
    en: PropTypes.string,
    es: PropTypes.string,
    de: PropTypes.string,
    nl: PropTypes.string,
  }).isRequired,
  setTextByLang: PropTypes.func.isRequired,
  savingFields: PropTypes.object.isRequired,
  saveTextChanges: PropTypes.func.isRequired,
  translateText: PropTypes.func.isRequired,
  pageId: PropTypes.string.isRequired,
  bookId: PropTypes.string,
  layoutItems: PropTypes.array,
  onAddToLayout: PropTypes.func,
  onToggleLayoutBox: PropTypes.func,
  layoutBusy: PropTypes.bool,
  additionalEntries: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    texts: PropTypes.object,
  })),
  onUpdateAdditionalEntryText: PropTypes.func,
  onSaveAdditionalEntries: PropTypes.func,
  onRemoveAdditionalEntry: PropTypes.func,
  onRenameAdditionalEntry: PropTypes.func,
  renamingText: PropTypes.bool,
};

export default EditableTextInputs;
